import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import "../ScheduleModal/scheduleModal.css";
import "./tiktokPostModal.css";

// TikTok's posting screen. Its layout follows TikTok's Content Sharing
// Guidelines, which the app audit checks: show the creator's TikTok account,
// let them pick who can see the post (no default), leave comment/duet/stitch
// off until they turn them on, offer the commercial content disclosure, and
// show the music/branded content declaration. Admin-only until approval.

const PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: "Everyone",
  MUTUAL_FOLLOW_FRIENDS: "Friends",
  FOLLOWER_OF_CREATOR: "Followers",
  SELF_ONLY: "Only me",
};

const MUSIC_URL = "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
const BC_POLICY_URL = "https://www.tiktok.com/legal/page/global/bc-policy/en";

export default function TikTokPostModal({ clip, clipJobId, clipIndex, initialMode = "direct", onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState("");
  const [title, setTitle] = useState(clip?.caption || clip?.hook || clip?.title || "");
  const [privacy, setPrivacy] = useState("");
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [disclose, setDisclose] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState(null);

  const videoUrl = clip?.editedUrl || clip?.url;

  useEffect(() => {
    axiosInstance
      .get("/tiktok/creator-info")
      .then((res) => setInfo(res.data))
      .catch((err) => setInfoError(err.response?.data?.error || "Couldn't load your TikTok account."));
  }, []);

  const tooLong = info?.maxVideoPostDurationSec && clip?.duration > info.maxVideoPostDurationSec;
  const discloseIncomplete = disclose && !yourBrand && !brandedContent;

  let blockReason = "";
  if (!info) blockReason = "Loading your TikTok account…";
  else if (mode === "direct" && tooLong) blockReason = `Your TikTok account allows videos up to ${info.maxVideoPostDurationSec} seconds.`;
  else if (mode === "direct" && !privacy) blockReason = "Choose who can see this post.";
  else if (mode === "direct" && discloseIncomplete) blockReason = "Choose whether this promotes your brand, a third party, or both.";

  const post = async () => {
    if (blockReason || posting) return;
    setPosting(true);
    try {
      const res = await axiosInstance.post("/tiktok/post", {
        clipUrl: videoUrl,
        clipTitle: clip?.hook || clip?.title || "",
        clipDuration: clip?.duration,
        clipJobId: clipJobId || null,
        clipIndex: clipIndex ?? 0,
        mode,
        postInfo: mode === "direct"
          ? { title, privacyLevel: privacy, allowComment, allowDuet, allowStitch, yourBrand: disclose && yourBrand, brandedContent: disclose && brandedContent }
          : undefined,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "TikTok post failed");
    } finally {
      setPosting(false);
    }
  };

  const label = brandedContent && disclose
    ? "Your video will be labeled as “Paid partnership”."
    : yourBrand && disclose
      ? "Your video will be labeled as “Promotional content”."
      : "";

  return (
    <div className="schedule-modal-overlay" onClick={posting ? undefined : onClose}>
      <div className="schedule-modal tt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h3>TikTok</h3>
          <button className="schedule-modal-close" onClick={onClose} disabled={posting} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {result ? (
          <div className="tt-result">
            {result.status === "in_drafts" ? (
              <>
                <h4>Sent to your TikTok drafts</h4>
                <p>Open the TikTok app and tap the notification (or your inbox) to edit and post it.</p>
              </>
            ) : result.status === "processing" ? (
              <>
                <h4>Uploaded to TikTok</h4>
                <p>TikTok is still processing it. It can take a few minutes to appear on your profile.</p>
                <a href={result.profileUrl} target="_blank" rel="noopener noreferrer">Open your TikTok profile</a>
              </>
            ) : (
              <>
                <h4>Posted to TikTok</h4>
                <p>It can take a few minutes for TikTok to finish processing and show it on your profile.</p>
                <a href={result.postUrl} target="_blank" rel="noopener noreferrer">View on TikTok</a>
              </>
            )}
            <button className="schedule-result-done" onClick={onClose}>Done</button>
          </div>
        ) : (
          <div className="tt-body">
            <div className="tt-tabs" role="tablist">
              <button role="tab" aria-selected={mode === "direct"} className={mode === "direct" ? "active" : ""} onClick={() => setMode("direct")} disabled={posting}>
                Post to TikTok
              </button>
              <button role="tab" aria-selected={mode === "draft"} className={mode === "draft" ? "active" : ""} onClick={() => setMode("draft")} disabled={posting}>
                Send to TikTok drafts
              </button>
            </div>

            {infoError ? (
              <p className="tt-error">{infoError}</p>
            ) : (
              <div className="tt-account">
                {info?.avatarUrl ? <img src={info.avatarUrl} alt="" /> : <span className="tt-avatar-placeholder" />}
                <div>
                  <strong>{info?.nickname || "Loading…"}</strong>
                  {info?.username && <span>@{info.username}</span>}
                </div>
              </div>
            )}

            <div className="tt-preview">
              {videoUrl && <video src={videoUrl} controls muted playsInline preload="metadata" />}
            </div>

            {mode === "draft" ? (
              <p className="tt-note">
                We'll send this clip to your TikTok inbox. Open the TikTok app to add sounds, text or effects, then post it from there.
              </p>
            ) : (
              <>
                <label className="tt-field">
                  <span>Caption</span>
                  <textarea value={title} onChange={(e) => setTitle(e.target.value)} maxLength={2200} rows={3} />
                  <small>{title.length}/2200</small>
                </label>

                <label className="tt-field">
                  <span>Who can see this post</span>
                  <select value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                    <option value="" disabled>Choose…</option>
                    {(info?.privacyLevelOptions || []).map((opt) => {
                      const blocked = opt === "SELF_ONLY" && disclose && brandedContent;
                      return (
                        <option key={opt} value={opt} disabled={blocked}>
                          {PRIVACY_LABELS[opt] || opt}{blocked ? " (not allowed for branded content)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <fieldset className="tt-field">
                  <span>Allow users to</span>
                  <div className="tt-checks">
                    <label className={info?.commentDisabled ? "disabled" : ""}>
                      <input type="checkbox" checked={allowComment} disabled={!info || info.commentDisabled} onChange={(e) => setAllowComment(e.target.checked)} />
                      Comment
                    </label>
                    <label className={info?.duetDisabled ? "disabled" : ""}>
                      <input type="checkbox" checked={allowDuet} disabled={!info || info.duetDisabled} onChange={(e) => setAllowDuet(e.target.checked)} />
                      Duet
                    </label>
                    <label className={info?.stitchDisabled ? "disabled" : ""}>
                      <input type="checkbox" checked={allowStitch} disabled={!info || info.stitchDisabled} onChange={(e) => setAllowStitch(e.target.checked)} />
                      Stitch
                    </label>
                  </div>
                </fieldset>

                <div className="tt-field tt-disclose">
                  <label className="tt-switch">
                    <span>
                      Disclose video content
                      <small>Turn on to show this video promotes goods or services in exchange for something of value.</small>
                    </span>
                    <input type="checkbox" checked={disclose} onChange={(e) => setDisclose(e.target.checked)} />
                  </label>
                  {disclose && (
                    <div className="tt-disclose-options">
                      <label>
                        <input type="checkbox" checked={yourBrand} onChange={(e) => setYourBrand(e.target.checked)} />
                        <span>
                          Your brand
                          <small>You're promoting yourself or your own business.</small>
                        </span>
                      </label>
                      <label title={privacy === "SELF_ONLY" ? "Branded content visibility can't be set to Only me." : ""} className={privacy === "SELF_ONLY" ? "disabled" : ""}>
                        <input type="checkbox" checked={brandedContent} disabled={privacy === "SELF_ONLY"} onChange={(e) => setBrandedContent(e.target.checked)} />
                        <span>
                          Branded content
                          <small>You're promoting another brand or a third party.</small>
                        </span>
                      </label>
                      {label && <p className="tt-label-note">{label}</p>}
                    </div>
                  )}
                </div>
              </>
            )}

            <p className="tt-declaration">
              By posting, you agree to TikTok's{" "}
              {mode === "direct" && disclose && brandedContent && (
                <>
                  <a href={BC_POLICY_URL} target="_blank" rel="noopener noreferrer">Branded Content Policy</a> and{" "}
                </>
              )}
              <a href={MUSIC_URL} target="_blank" rel="noopener noreferrer">Music Usage Confirmation</a>.
            </p>

            {blockReason && info && <p className="tt-hint">{blockReason}</p>}

            <div className="schedule-actions">
              <button type="button" className="schedule-cancel" onClick={onClose} disabled={posting}>Cancel</button>
              <button type="button" className="schedule-submit" onClick={post} disabled={!!blockReason || posting || !!infoError}>
                {posting ? (
                  <>
                    <span className="schedule-btn-spinner" />
                    {mode === "draft" ? "Sending…" : "Posting…"}
                  </>
                ) : mode === "draft" ? "Send to drafts" : "Post"}
              </button>
            </div>
            {posting && <p className="tt-note">Uploading to TikTok. This can take a minute; keep this window open.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
